// ============================================
// src/App/receiptConsentGate.js
// MA-61 teacher-receipt-view slice — teacher consent gate for viewing a
// linked student's Misconception Receipt (pure core, mirrors
// schoolConsentGate.js's shape exactly).
//
// WHY A SEPARATE GATE (not reuse of MA-60's school-authority consent): that
// consent's copy is scoped to roster ENTRY ("...authorization to enter
// these students' names for this class roster.") — it never told the
// teacher (or, transitively, the school) that the teacher would also see a
// student's practice attempt/misconception history. Per the standing
// build-loop rule, new/changed disclosure copy is the one thing this loop
// does not autonomously ship live text for — the exact wording is a DRAFT
// pending founder/legal sign-off (see docs/legal/PENDING-COPY.md); the CODE
// PATH (the gate + its wiring) ships now and fails closed to "not
// consented" until the copy is approved.
//
// Reuses the SAME `consent_records` table + RLS (`parent_id = auth.uid()`)
// as schoolConsentGate.js — a teacher's own authenticated session already
// satisfies the existing INSERT/SELECT policies with no schema change.
//
// SCOPE (MA-937, D-TUX-25 — rewritten from the per-student-only rationale).
// Two rows satisfy this gate:
//   • the per-student row `receipt_view_v1:<studentId>` (every existing row
//     keeps working), and
//   • the CLASS row `class_practice_v1:<classId>` (defined here, shared with
//     assignConsentGate.js), for a child with a LIVE enrollment in that class
//     (an un-revoked roster row naming the child), children added later
//     included.
// The per-student scope existed because a teacher's view of a linked child
// used to include that child's HOME data (sessions, milestones, home
// attempts), a specific family's record the class roster never vouched for,
// and because a revoked roster row can later be re-claimed by a different
// child. Once MA-936's migration (0063) is applied the database admits a
// teacher only the child's IN-CLASS practice, in the teacher's own class, while
// the enrollment is live — practice the school's own use of PencilWise
// produced. A class-level confirmation of that authority is then the honest
// unit (counsel's MA-61 question 3 asks exactly this cadence; this adopts it
// pending the answer), and the re-claim concern is met because the class row
// only covers a child the CURRENT roster lists live: a revoked row covers no
// one. So the class row counts ONLY when the MA-936 lane read answers "live"
// (teacher_linked_students answered; a 42703 / PGRST202 / 42883 answer means
// 0063 is not applied and the per-student cards stay; "pending"/"unknown"
// fail closed). CLASS_PRACTICE_ACK_ENABLED = false turns the class row off and
// brings the per-student cards back if counsel requires them.
// ============================================

/** MA-937 — the class acknowledgement switch (both gates read this one). */
export const CLASS_PRACTICE_ACK_ENABLED = true;

export const CLASS_PRACTICE_CONSENT_PREFIX = "class_practice_v1";

/** `class_practice_v1:<classId>` — the value written to/matched against consent_type. */
export function classPracticeConsentType(classId) {
  return `${CLASS_PRACTICE_CONSENT_PREFIX}:${classId}`;
}

function isActiveRowOf(r, type) {
  return (
    !!r &&
    r.granted === true &&
    (r.revoked_at === null || r.revoked_at === undefined) &&
    r.consent_type === type
  );
}

/** True iff `rows` holds a granted, un-revoked class-practice row for EXACTLY this class. */
export function hasClassPracticeConsent(rows, classId) {
  if (!Array.isArray(rows) || !classId) return false;
  const type = classPracticeConsentType(classId);
  return rows.some((r) => isActiveRowOf(r, type));
}

/**
 * Child id -> the class ids holding a LIVE enrollment for it, from roster rows
 * the teacher area already reads (`class_enrollments`): a row counts only with
 * a `student_id`, a `class_id`, no `revoked_at` and no `removed_at` (MA-939: a
 * child removed from the class list is not live; `undefined` before migration
 * 0065 reads as not removed).
 */
/** @param {any} roster @returns {Record<string, string[]>} */
export function liveEnrollmentsFromRoster(roster) {
  /** @type {Record<string, string[]>} */
  const out = {};
  if (!Array.isArray(roster)) return out;
  for (const row of roster) {
    if (!row || !row.student_id || !row.class_id) continue;
    if (row.revoked_at !== null && row.revoked_at !== undefined) continue;
    if (row.removed_at !== null && row.removed_at !== undefined) continue;
    const list = out[row.student_id] ?? (out[row.student_id] = []);
    if (!list.includes(row.class_id)) list.push(row.class_id);
  }
  return out;
}

/** True when a class row may count: the switch is on AND the MA-936 lane read
 *  (`{ state }`, classLane.ts) answered "live" (0063 applied). */
/** @param {{ state: string } | null | undefined} lane @param {boolean} [enabled] */
export function classPracticeLive(lane, enabled = CLASS_PRACTICE_ACK_ENABLED) {
  return enabled === true && !!lane && lane.state === "live";
}

/** True iff a class-practice row covers this child: the class row may count
 *  (classPracticeLive) and one of the child's live-enrollment classes holds a
 *  granted, un-revoked class_practice_v1 row. */
/**
 * @param {any} rows
 * @param {string} studentId
 * @param {Readonly<Record<string, readonly string[]>> | null | undefined} liveEnrollments
 * @param {{ state: string } | null | undefined} lane
 * @param {boolean} [enabled]
 */
export function coveredByClassPractice(rows, studentId, liveEnrollments, lane, enabled = CLASS_PRACTICE_ACK_ENABLED) {
  if (!classPracticeLive(lane, enabled) || !studentId || !liveEnrollments) return false;
  const classIds = liveEnrollments[studentId];
  if (!Array.isArray(classIds)) return false;
  return classIds.some((classId) => hasClassPracticeConsent(rows, classId));
}

export const RECEIPT_CONSENT_PREFIX = "receipt_view_v1";

/** `receipt_view_v1:<studentId>` — the value written to/matched against consent_type. */
export function receiptConsentType(studentId) {
  return `${RECEIPT_CONSENT_PREFIX}:${studentId}`;
}

/**
 * True iff `rows` contains a GRANTED, un-revoked receipt-view consent row
 * scoped to EXACTLY this `studentId`, OR (MA-937) a class-practice row covers
 * the child (coveredByClassPractice: `liveEnrollments` = child id -> live
 * class ids, `lane` = the MA-936 lane read). A consent granted for a different
 * student (or a non-receipt consent, e.g. `school_authority_v1_*`) never
 * satisfies this gate; without the optional arguments it is the per-student
 * gate exactly.
 * @param {any} rows
 * @param {string} studentId
 * @param {Readonly<Record<string, readonly string[]>> | null} [liveEnrollments]
 * @param {{ state: string } | null} [lane]
 * @param {boolean} [enabled]
 * @returns {boolean}
 */
export function hasReceiptViewConsent(rows, studentId, liveEnrollments = null, lane = null, enabled = CLASS_PRACTICE_ACK_ENABLED) {
  if (!Array.isArray(rows)) return false;
  const type = receiptConsentType(studentId);
  if (rows.some((r) => isActiveRowOf(r, type))) return true;
  return coveredByClassPractice(rows, studentId, liveEnrollments, lane, enabled);
}

/**
 * The gate decision. Dev bypass short-circuits (e2e/dev has no real
 * session) — SAME policy as lane (a)/(b)'s guards. A read error is treated
 * as NO consent (fail-CLOSED): a flaky fetch must never open the gate.
 */
export async function guardReceiptView({ studentId, teacherId, fetchConsent, devBypass = false, liveEnrollments = null, lane = null }) {
  if (devBypass) return { allow: true, reason: "dev-bypass" };
  let rows = [];
  try {
    rows = await fetchConsent(teacherId);
  } catch {
    rows = [];
  }
  return hasReceiptViewConsent(rows, studentId, liveEnrollments, lane)
    ? { allow: true, reason: "consented" }
    : { allow: false, reason: "no-consent" };
}
